import { forkJoin, from, of } from 'rxjs';
import { concatMap, delay, first, map, mergeMap, switchMap } from 'rxjs/operators';
import { EntityUIState } from '../../core/interfaces/entitiy-ui-state';
import { wrapIntoObservable } from '../../wizard/wizard-native/utils';
import { useQuery } from '../../core/hooks/useQuery';

export function useResolvedComponent({
  parallelExecution = false,
  canActivate,
  resolve
}: {
    parallelExecution?: boolean;
    canActivate?: Array<() => Promise<boolean> | boolean>;
    resolve?: { [key: string]: any };
    
}) {
  const {
    data,
    uiState,
    error,
    fetch: resolveComponent
  } = useQuery({
    dataSource$: () =>
      from(canActivate || [])
        .pipe(
          delay(0),
          (parallelExecution ? mergeMap : concatMap)(guard =>
            wrapIntoObservable(guard()).pipe(first())
          ), // Use mergeMap for parallel, concatMap for sequential
          first(result => result !== true, true),
          switchMap(guardsResult => {
            if (!guardsResult || !resolve || !Object.keys(resolve || {}).length) {
              return of({
                guardsResult
              });
            }

            return forkJoin(
              Object.entries(resolve).reduce(
                (dict, [key, fn]) => ({
                  ...dict,
                  [key]: wrapIntoObservable(fn())
                }),
                {}
              )
            ).pipe(
              map(d => ({
                guardsResult,
                resolvedData: d
              }))
            );
          })
        )
        .toPromise()
  });

  const resolvingRoute = uiState === EntityUIState.LOADING || uiState === EntityUIState.NEW;
  const canActivateRoute = uiState === EntityUIState.IDLE && data?.guardsResult;
  const routeActivationError = uiState === EntityUIState.ERRORED ? error : null;
  const resolvedData = data?.resolvedData;

  return {
    resolveComponent,
    resolvedData,
    resolvingRoute,
    canActivateRoute,
    routeActivationError
  };
}
